import { createModel, useRestQuery } from '@kwai-explore/model';
import { computed, inject, nextTick, onMounted, ref } from 'vue';
import { RouteName } from '@/router';
import { shelfCommodityInnerControllerListShelfCommodityInfo } from '@api/eCommerce/paths/schemas';
import { getTimestampByHour } from '@/utils';
import { useLocalStorage } from '@vueuse/core';
import type { CommerceItem } from '@pet/fish.commerce-card-list/types';
import { CardClickArea } from '@pet/fish.commerce-card-list/types';
import type { ComputedRef } from 'vue-demi';
import { useOpenPage } from '@pet/base.open-page';
import { invoke } from '@yoda/bridge';
import { formatCardInfo } from '../../utils/formatCard';
import { EOrderSourceEnum } from '@/types/e-commerce';
import { useKconf } from '@pex/ug-ssr-server/hooks/use-kconf';
import { getChannelFromQuery } from '@/pages/e-commerce/utils/utils';

const END_TIME_HOUR = 1;

export const HomeModel = createModel(() => {
    const openPage = useOpenPage();
    const kconf = useKconf();
    const { info, refetch } = useRestQuery<
        shelfCommodityInnerControllerListShelfCommodityInfo.Response['200']['*/*']['data']
    >({
        url: shelfCommodityInnerControllerListShelfCommodityInfo.path,
        method: shelfCommodityInnerControllerListShelfCommodityInfo.method,
        headers: { 'content-type': 'application/json' },
        skip: (route) => route.name !== RouteName.E_COMMERCE.HOME,
        variables(route) {
            return {
                orderSource: route.query.orderSource ?? EOrderSourceEnum.E_COMMERCE,
                channelName: getChannelFromQuery(route),
                kpn: route?.query.kpn === 'NEBULA' ? 'NEBULA' : 'KUAISHOU',
                itemId: route?.query.itemIds,
            };
        },
    });

    const useSSR: boolean | undefined = inject('useSSR');
    onMounted(async () => {
        if (useSSR) {
            await nextTick();
            refetch();
        }
    });

    const showCountDown = ref(false);

    const endTime = useLocalStorage('e-commerce-endTime', getTimestampByHour(END_TIME_HOUR));

    onMounted(() => {
        if (endTime.value < Date.now()) {
            endTime.value = getTimestampByHour(END_TIME_HOUR);
        }
        showCountDown.value = true;
    });

    const refreshEndTime = async () => {
        setTimeout(() => {
            endTime.value = getTimestampByHour(END_TIME_HOUR);
        }, 1000);
    };

    const data = computed(() => {
        return info.data;
    });

    const isLogin = computed(() => {
        return data.value?.login ?? false;
    });

    const commodityList: ComputedRef<CommerceItem[]> = computed(() => formatCardInfo(data.value?.itemVoList ?? []));

    const mainTitle = computed(() => data.value?.mainTitle ?? '');

    const openOrderFloatingLayer = (item: CommerceItem) => {
        openPage(item.itemOrderUrlKrn ?? '');
    };

    const gotoOrderPage = (item: CommerceItem) => {
        openPage(item.itemJumpUrl ?? '', { forceOpenInNewWebview: true });
    };

    const gotoLogin = () => {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
            try {
                const { result } = await invoke('social.login', {
                    checkFromServer: true,
                    loginSource: 51,
                });
                resolve(true);
            } catch (e) {
                invoke('ui.showToast', {
                    type: 'normal',
                    text: '登录失败',
                });
                reject(false);
            }
        });
    };

    const gotoOrder = async (item: CommerceItem, action: CardClickArea) => {
        // 如果点击了"抢按钮"，且有提单链接，走提单支付流程 TODO: 替换为提单支付浮层链接
        if (action === CardClickArea.BUTTON && item.itemOrderUrlKrn) {
            // 如果未登录，走登录逻辑，登录成功后直接拉起浮层
            if (!isLogin.value) {
                const loginResult = await gotoLogin();
                // 登录成功，刷新主接口
                if (loginResult) {
                    await refetch();
                    openOrderFloatingLayer(item);
                }
            } else {
                // 如果已登录，直接拉起浮层
                openOrderFloatingLayer(item);
            }
            return;
        }
        // 其他场景直接跳商详页
        gotoOrderPage(item);
    };

    const pageInfo = computed(() => kconf?.['userGrowth.frontend.eCommerceInfo']);

    const exitPage = async () => {
        try {
            openPage(pageInfo.value?.kwaiMerchantHomeLink);
            await invoke('webview.exitCurrentWebView');
        } catch (e) {
            await invoke('webview.exitCurrentWebView');
        }
    };

    return {
        info,
        commodityList,
        isLogin,
        endTime,
        refreshEndTime,
        mainTitle,
        gotoOrder,
        exitPage,
        showCountDown,
    };
});
